import React from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { MoneyField2 } from "../../fields/MoneyField2";
import { SectionAccordion } from "./SectionAccordion";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import {
  SoftCostSectionProps,
  ChangeCountSoftCostSectionProps,
} from "../../../../types";
import { PAType } from "../../ProfitAnalysisPage";

export interface SoftCostSectionComponentProps {
  state: SoftCostSectionProps;
  setState: (state: SoftCostSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  totalBL: number;
  totalBP: number;
  mode: "edit" | "review";
  pageVariant: "create" | "edit" | "view";
  marketAdjustment?: string;
  paType?: PAType;
  blPercent?: number;
  contractPrice?: number;
  disableCommissions?: boolean;
  initialState?: SoftCostSectionProps;
  changeCount?: ChangeCountSoftCostSectionProps;
  forceMobile?: boolean;
  bpRealtorCommissions?: number;
  bpInHouseCommissions?: number;
  bpBuilderBonus?: number;
  blRealtorCommissions?: number;
  blInHouseCommissions?: number;
  blBuilderBonus?: number;
}

export function SoftCostSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  totalBL,
  totalBP,
  mode,
  pageVariant,
  marketAdjustment = "",
  paType,
  blPercent,
  contractPrice,
  disableCommissions,
  initialState,
  changeCount,
  forceMobile,
  bpRealtorCommissions,
  bpInHouseCommissions,
  bpBuilderBonus,
  blRealtorCommissions,
  blInHouseCommissions,
  blBuilderBonus,
}: SoftCostSectionComponentProps) {

  const calcRealtor = Math.round(((contractPrice ?? 0) * 3) / 100);
  const calcInHouse = Math.round(((contractPrice ?? 0) * 2) / 100);
  const calcBuilderBonus = Math.round(((contractPrice ?? 0) * 0.5) / 100);

  React.useEffect(() => {
    if (!disableCommissions) return;
    if (
      state.RealtorCommissions === calcRealtor &&
      state.InHouseCommissions === calcInHouse &&
      state.BuilderBonus === calcBuilderBonus
    ) return;
    setState({
      ...state,
      RealtorCommissions: calcRealtor,
      InHouseCommissions: calcInHouse,
      BuilderBonus: calcBuilderBonus,
    });
  }, [disableCommissions, calcRealtor, calcInHouse, calcBuilderBonus]);

  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  const dollarAdornment = <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment>;
  const negDollarAdornment = <InputAdornment position="start">-$</InputAdornment>;

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }
  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  function ViewField({ value, adornment = dollarAdornment }: { value: number | undefined; adornment?: React.ReactNode }) {
    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        disabled
        value={value ?? ""}
        InputProps={{ startAdornment: adornment }}
        sx={viewFieldSx}
      />
    );
  }

  return (
    <SectionAccordion
      id="softCost"
      title="Soft Cost"
      icon={<RequestQuoteIcon fontSize="large" sx={{ color: "#ACACAC" }} />}
      total={total}
      forceMobile={forceMobile}
    >
      {/* Lot Cost */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Lot Cost/(Code F)(Note 2)(+/1 Deviation)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.LotCost)}{" "}
            {changeCountText(changeCount?.LotCost ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.LotCost} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.LotCost === undefined ? "" : state.LotCost.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("LotCost", e.target.value)}
            error={formErrors["LotCost"] !== ""}
            helperText={formErrors["LotCost"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Lot Cost/(Code F)(Note 2)(+/1 Deviation)"
            value={state.LotCost ?? null}
            onChange={(v) => updateField("LotCost", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["LotCost"] !== ""}
            helperText={formErrors["LotCost"]}
          />
        )}
      </Grid>

      {/* Discretionary */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Discretionary - Buyer Closing Costs paid by Seller
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.Discretionary)}{" "}
            {changeCountText(changeCount?.Discretionary ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.Discretionary} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.Discretionary === undefined ? "" : state.Discretionary.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("Discretionary", e.target.value)}
            error={formErrors["Discretionary"] !== ""}
            helperText={formErrors["Discretionary"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Discretionary - Buyer Closing Costs paid by Seller"
            value={state.Discretionary ?? null}
            onChange={(v) => updateField("Discretionary", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["Discretionary"] !== ""}
            helperText={formErrors["Discretionary"]}
          />
        )}
      </Grid>

      {/* Has Realtor? */}
      <Grid item xs={labelXs} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ textAlign: "start", mt: 2 }}>
          Has Realtor?
        </Typography>
        <FormControl>
          <FormLabel sx={{ textAlign: "start", mt: 1, ml: 1 }}></FormLabel>
        </FormControl>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ textAlign: "start", mt: 2, ml: 1 }}>
            {initialState?.HasRealtor === undefined ? "Yes" : initialState.HasRealtor ? "Yes" : "No"}{" "}
            {(changeCount?.HasRealtor ?? 1) > 1 ? `(${changeCount?.HasRealtor})` : ""}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        <FormControl>
          <FormLabel sx={{ textAlign: "start", mt: 1, ml: 1 }}></FormLabel>
          <RadioGroup
            row
            value={state.HasRealtor ? "true" : "false"}
            onChange={(e) => {
              setState({
                ...state,
                HasRealtor: e.target.value === "true",
                RealtorCommissions: e.target.value === "true" ? state.RealtorCommissions : 0,
              });
            }}
          >
            <FormControlLabel value="true" control={<Radio disabled={mode === "review" || isView} />} label="Yes" sx={{ variant: "body2", color: "text.secondary", fontWeight: 400 }} />
            <FormControlLabel value="false" control={<Radio disabled={mode === "review" || isView} />} label="No" sx={{ variant: "body2", color: "text.secondary", fontWeight: 400 }} />
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Realtor Commissions */}
      <Grid item xs={labelXs} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          Realtor Commissions-(3%)/(Category H)
        </Typography>
        <FormControl>
          <FormLabel sx={{ textAlign: "start", mt: 1, ml: 1 }}></FormLabel>
        </FormControl>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {paType === "sold" ? initialText(initialState?.RealtorCommissions) : ""}
            {paType === "sold" ? changeCountText(changeCount?.RealtorCommissions ?? 0) : ""}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {paType === "sold" ? (
          isView ? (
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              disabled
              value={disableCommissions ? String(calcRealtor) : (state.RealtorCommissions ?? "")}
              InputProps={{ startAdornment: dollarAdornment }}
              sx={{ ...viewFieldSx, visibility: state.HasRealtor ? "visible" : "hidden" }}
            />
          ) : (
            <TextField
              variant="outlined"
              size="small"
              type="text"
              inputProps={{ inputMode: "numeric", min: 0 }}
              sx={{ visibility: state.HasRealtor ? "visible" : "hidden" }}
              value={disableCommissions ? String(calcRealtor) : (state.RealtorCommissions === undefined ? "" : state.RealtorCommissions.toString().replace(/^0+/, ""))}
              disabled={disableCommissions || mode === "review"}
              onChange={(e) => updateField("RealtorCommissions", e.target.value)}
              error={formErrors["RealtorCommissions"] !== ""}
              helperText={formErrors["RealtorCommissions"]}
              fullWidth
              InputProps={{ startAdornment: dollarAdornment }}
            />
          )
        ) : (
          <MoneyField2
            label="Realtor Commissions-(3%)/(Category H)"
            value1={disableCommissions ? (paType === "spec" ? Math.round(blRealtorCommissions) : calcRealtor) : (Math.round(state.RealtorCommissions) ?? null)}
            value1Label="BL"
            value2={disableCommissions ? (paType === "spec" ? Math.round(bpRealtorCommissions) : calcRealtor) : (Math.round(state.RealtorCommissions) ?? null)}
            value2Label="BP"
            disabled={disableCommissions || mode === "review"}
            allowNegative={false}
          />
        )}
      </Grid>

      {/* Market Adjustment (SpecialMarketingIncentive) */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Realtor Incentive
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.SpecialMarketingIncentive)}{" "}
            {changeCountText(changeCount?.SpecialMarketingIncentive ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.SpecialMarketingIncentive} />
        ) : (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "decimal", pattern: "^-?\\d{1,7}(\\.\\d{1,4})?$", min: -9999999.99 }}
            value={state.SpecialMarketingIncentive === undefined ? "" : marketAdjustment}
            onChange={(e) => {
              const value = e.target.value;
              if (/^-?\d{0,7}(\.\d{0,4})?$/.test(value) || value === "") {
                updateField("SpecialMarketingIncentive", value);
              }
            }}
            error={formErrors["SpecialMarketingIncentive"] !== ""}
            helperText={formErrors["SpecialMarketingIncentive"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        )}
      </Grid>

      {/* Realtor Commissions adjusted */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Realtor Commissions adjusted
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.RealtorCommissionsadjusted)}{" "}
            {changeCountText(changeCount?.RealtorCommissionsadjusted ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.RealtorCommissionsadjusted} adornment={negDollarAdornment} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.RealtorCommissionsadjusted === undefined ? "" : state.RealtorCommissionsadjusted.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("RealtorCommissionsadjusted", e.target.value)}
            error={formErrors["RealtorCommissionsadjusted"] !== ""}
            helperText={formErrors["RealtorCommissionsadjusted"]}
            fullWidth
            InputProps={{ startAdornment: negDollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Realtor Commissions adjusted"
            value={state.RealtorCommissionsadjusted ?? null}
            onChange={(v) => updateField("RealtorCommissionsadjusted", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["RealtorCommissionsadjusted"] !== ""}
            helperText={formErrors["RealtorCommissionsadjusted"]}
          />
        )}
      </Grid>

      {/* In-House Commissions */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          In-House Commissions-(2%)/(Category I)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {paType === "sold" ? initialText(initialState?.InHouseCommissions) : ""}{" "}
            {paType === "sold" ? changeCountText(changeCount?.InHouseCommissions ?? 0) : ""}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {paType === "sold" ? (
          isView ? (
            <ViewField value={disableCommissions ? calcInHouse : state.InHouseCommissions} />
          ) : (
            <TextField
              disabled={disableCommissions || mode === "review"}
              variant="outlined"
              size="small"
              type="text"
              inputProps={{ inputMode: "numeric", min: 0 }}
              value={disableCommissions ? String(calcInHouse) : (state.InHouseCommissions === undefined ? "" : state.InHouseCommissions.toString().replace(/^0+/, ""))}
              onChange={(e) => updateField("InHouseCommissions", e.target.value)}
              error={formErrors["InHouseCommissions"] !== ""}
              helperText={formErrors["InHouseCommissions"]}
              fullWidth
              InputProps={{ startAdornment: dollarAdornment }}
            />
          )
        ) : (
          <MoneyField2
            label="In-House Commissions-(2%)/(Category I)"
            value1={disableCommissions ? (paType === "spec" ? Math.round(blInHouseCommissions) : calcInHouse) : (Math.round(state.InHouseCommissions) ?? null)}
            value1Label="BL"
            value2={disableCommissions ? (paType === "spec" ? Math.round(bpInHouseCommissions) : calcInHouse) : (Math.round(state.InHouseCommissions) ?? null)}
            value2Label="BP"
            disabled={disableCommissions || mode === "review"}
            allowNegative={false}
          />
        )}
      </Grid>

      {/* Interim Interest Budget */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Interim Interest Budget(#8140)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.InterimInterestBudget)}{" "}
            {changeCountText(changeCount?.InterimInterestBudget ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.InterimInterestBudget} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.InterimInterestBudget === undefined ? "" : state.InterimInterestBudget.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("InterimInterestBudget", e.target.value)}
            error={formErrors["InterimInterestBudget"] !== ""}
            helperText={formErrors["InterimInterestBudget"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Interim Interest Budget(#8140)"
            value={state.InterimInterestBudget ?? null}
            onChange={(v) => updateField("InterimInterestBudget", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["InterimInterestBudget"] !== ""}
            helperText={formErrors["InterimInterestBudget"]}
          />
        )}
      </Grid>

      {/* Closing Cost Budget */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Closing Cost Budget/(Category K)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.ClosingCostBudget)}{" "}
            {changeCountText(changeCount?.ClosingCostBudget ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.ClosingCostBudget} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.ClosingCostBudget === undefined ? "" : state.ClosingCostBudget.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("ClosingCostBudget", e.target.value)}
            error={formErrors["ClosingCostBudget"] !== ""}
            helperText={formErrors["ClosingCostBudget"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Closing Cost Budget/(Category K)"
            value={state.ClosingCostBudget ?? null}
            onChange={(v) => updateField("ClosingCostBudget", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["ClosingCostBudget"] !== ""}
            helperText={formErrors["ClosingCostBudget"]}
          />
        )}
      </Grid>

      {/* Builder Bonus */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Builder Bonus (#8195)
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {paType === "sold" ? initialText(initialState?.BuilderBonus) : ""}{" "}
            {paType === "sold" ? changeCountText(changeCount?.BuilderBonus ?? 0) : ""}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {paType === "sold" ? (
          isView ? (
            <ViewField value={disableCommissions ? calcBuilderBonus : state.BuilderBonus} />
          ) : (
            <TextField
              disabled={disableCommissions || mode === "review"}
              variant="outlined"
              size="small"
              type="text"
              inputProps={{ inputMode: "numeric", min: 0 }}
              value={disableCommissions ? calcBuilderBonus : (state.BuilderBonus === undefined ? "" : state.BuilderBonus.toString().replace(/^0+/, ""))}
              onChange={(e) => updateField("BuilderBonus", e.target.value)}
              error={formErrors["BuilderBonus"] !== ""}
              helperText={formErrors["BuilderBonus"]}
              fullWidth
              InputProps={{ startAdornment: dollarAdornment }}
            />
          )
        ) : (
          <MoneyField2
            label="Builder Bonus (#8195)"
            value1={disableCommissions ? (paType === "spec" ? Math.round(blBuilderBonus) : calcBuilderBonus) : (Math.round(state.BuilderBonus) ?? null)}
            value1Label="BL"
            value2={disableCommissions ? (paType === "spec" ? Math.round(bpBuilderBonus) : calcBuilderBonus) : (Math.round(state.BuilderBonus) ?? null)}
            value2Label="BP"
            disabled={disableCommissions || mode === "review"}
            allowNegative={false}
          />
        )}
      </Grid>

      {/* Subtotal Soft Cost */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"} mt={5}>
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Subtotal Soft Cost
        </Typography>
      </Grid>
      <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
        {paType === "spec" ? (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                variant="filled"
                size="small"
                label="BL"
                inputProps={{ min: 0 }}
                disabled
                value={totalBL.toFixed(0)}
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="filled"
                size="small"
                label="BP"
                inputProps={{ min: 0 }}
                disabled
                value={totalBP.toFixed(0)}
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            </Grid>
          </Grid>
        ) : (
          <TextField
            variant="filled"
            size="small"
            inputProps={{ min: 0 }}
            disabled
            value={total}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        )}
      </Grid>

    </SectionAccordion>
  );
}
